import { useAppDispatch, useAppSelector } from 'flow/hooks/useRedux';
import React, { useEffect } from 'react';
import { useHistory } from 'react-router-dom';

import {
    fetchContact,
    fetchEditableContact,
} from 'flow/actions/contact-actions';
import Page from 'flow/components/Setting/Page';
import Icon from 'flow/components/ui-components/Icon';
import IconButton from 'flow/components/ui-components/IconButton';
import useAppContext from 'flow/hooks/useAppContext';
import {
    getContact,
    getContactKeyFromExtensionKey,
} from 'flow/selectors/contact-selectors';
import { selectCallRecordContact } from 'flow/selectors/call-records-selectors';
import { getExtensionKeyFromContactKey } from 'flow/selectors/ExtensionSelectors';
import { selectIsAdmin } from 'flow/selectors/own-extension-selectors';
import { getIsSinglePaneMode } from 'flow/selectors/selectors';
import { CallRecordKey, ContactKey, ExtensionKey } from 'flow/types/EntityKeys';
import { LocationType } from 'flow/types/Location';
import { routes } from 'flow/types/routes';
import { generatePath } from 'flow/utils/router-utils';
import ContactCallHistoryWidgetTW from './ContactCallHistoryWidgetTW';
import ContactHeader from './ContactHeader';
import ContactInfoCard from './ContactInfoCard';
import ContactSettingsCard from './ContactSettingsCard';
import ContactScheduleCard from './ContactScheduleCard';

interface Props {
    parentContactKey?: ContactKey;
    parentExtensionKey?: ExtensionKey;
    onNavigation?: () => void;
    callRecordKey?: CallRecordKey;
    calendarPath?: string;
}

const ContactContainer = ({
    parentContactKey,
    parentExtensionKey,
    callRecordKey,
    calendarPath,
    onNavigation,
}: Props) => {
    const { i18next, showDialpad, setShowDialpad } = useAppContext();
    const dispatch = useAppDispatch();
    const history = useHistory<LocationType>();

    const isAdmin = useAppSelector(selectIsAdmin);
    const isSinglePaneMode = useAppSelector(getIsSinglePaneMode);

    let contactKey = useAppSelector((state) =>
        getContactKeyFromExtensionKey(state, parentExtensionKey),
    );

    contactKey = parentContactKey || contactKey;

    let extensionKey = useAppSelector((state) =>
        //@ts-ignore
        getExtensionKeyFromContactKey(state, parentContactKey),
    );

    extensionKey = parentExtensionKey || extensionKey;

    const contact = useAppSelector(
        (state) =>
            getContact(state, contactKey, extensionKey) ||
            selectCallRecordContact(state, callRecordKey),
    );

    useEffect(() => {
        if (!contact?.get('editableContactDTO')) {
            dispatch(fetchContact(contact?.get('key')));
            dispatch(fetchEditableContact(contact?.get('key')));
        }
    }, [dispatch, contact]);

    const propsWithKeys = {
        contactKey,
        extensionKey,
        callRecordKey,
        contact,
        onNavigation,
    };

    const onClickSettingsOnBehalf = () => {
        if (showDialpad) {
            setShowDialpad(false);
        }

        history.push(
            generatePath(routes['settings'], {
                extensionKey,
            }),
        );
    };

    return (
        <Page
            childrenClassName="w-full"
            className="bg-gray-200 dark:bg-white"
            discardHeader={false}
            action={
                isAdmin &&
                extensionKey && (
                    <IconButton
                        tooltipLabel={i18next.t(
                            'settings_on_behalf.edit_user_settings',
                        )}
                        onClick={onClickSettingsOnBehalf}
                    >
                        <Icon iconKey="setting_menu" />
                    </IconButton>
                )
            }
            onClickBack={
                history.location.state?.from
                    ? () => history.push(history.location.state?.from)
                    : isSinglePaneMode
                      ? () => history.push(routes['extensions'])
                      : undefined
            }
        >
            <div className="flex flex-col items-center px-6">
                <div className="@container relative flex w-full max-w-screen-md flex-col gap-y-6">
                    <ContactHeader {...propsWithKeys} />
                    <div className="flex flex-col items-start gap-6 @2xl:flex-row">
                        <ContactInfoCard {...propsWithKeys} />

                        <div className="flex w-full min-w-1/2 flex-col gap-6 @2xl:w-auto">
                            <ContactSettingsCard {...propsWithKeys} />
                            <ContactScheduleCard
                                {...propsWithKeys}
                                calendarPath={calendarPath}
                            />
                            <ContactCallHistoryWidgetTW {...propsWithKeys} />
                        </div>
                    </div>
                </div>
            </div>
        </Page>
    );
};

export default React.memo(ContactContainer);
